import MidiEffect from '../MidiEffect.js';
import {IntegerParameter} from "parameters/AbstractParameter.js";
import {bulkUpdate} from "utils/bulkUpdate.js";
import {saveParameterToJson, loadParameterFromJson} from "parameters/parameterJsonFunctions.js";
import {getLeafString, dash, dot} from '../tidal/parser.js';
import {
    applyToLeaf,
    applyRule,
    REGULAR,
    before,
    after,
    when ,
    otherwise,
    TIMES,
    ACCENT,
    ANY,
    FLAM,
    DOUBLE,
    LEGATO,
    STACCATO,
    REST,
    PIANO,
    PIANISSIMO
} from '../scripting/trules.js';

class RuleEffect extends MidiEffect {
    constructor(name, track, type, action, aux) {
        super(name, track, 0);
        this.numberOfOutputs = 3;
        this.predicate = type;
        this.action = action;
        this.aux = aux;
    }

    getText() {
        if (this.value !== undefined) {
            return this.value;
        }
        return '';
    }
}


class AuxEffect extends RuleEffect  {
    constructor(name, track, aux) {
        super(name, track, undefined, undefined, aux);
        this.numberOfOutputs = 1;
        this.valueParameter = new IntegerParameter(
            'value',
            this,
            (o, x) => {
                if (o.rack) {
                    o.rack.updateRules();
                }
            },
            {
                defaultValue: undefined,
                min:-1,
                max: 16,
                exactAutomation: true
            });
        this.registerParameter(this.valueParameter);
    }

    getJson() {
        let json = {
            ... super.getJson(),
            value: this.value
        };

        json = saveParameterToJson(json, 'valueParameter', this.valueParameter);
        return json;
    }

    fromJson(json) {
        let bulk = super.fromJson(json);
        bulkUpdate(this.valueParameter, json.value, bulk);
        return {
            ... bulk,
            ... loadParameterFromJson(json, 'valueParameter', this.valueParameter)
        };
    }

    set value(x) {
        this.valueParameter.set(x, false, false);
    }

    get value() {
        return Math.round(this.valueParameter.get());
    }
}

export class Accent extends RuleEffect {
    constructor(track) {
        super("Accent", track, undefined, ACCENT);
    }
}

export class Flam extends RuleEffect {
    constructor(track) {
        super("Flam", track, undefined, FLAM);
    }
}

export class Regular extends RuleEffect {
    constructor(track) {
        super("Regular", track, undefined, REGULAR);
    }
}

export class Double extends RuleEffect {
    constructor(track) {
        super("Double", track, undefined, DOUBLE);
    }
}

export class Times extends RuleEffect {
    constructor(track) {
        super("Times", track, undefined, TIMES);
        this.valueParameter = new IntegerParameter(
            'value',
            this,
            (o, x) => {
                this.action = {
                    ... TIMES,
                    value: x
                };
                if (o.rack) {
                    o.rack.updateRules();
                }
            },
            {
                defaultValue: 3,
                min:1,
                max: 16,
                exactAutomation: true
            });
        this.registerParameter(this.valueParameter);
    }
    
    set value(x) {
        this.valueParameter.set(x, false, false);
    }

    get value() {
        return Math.round(this.valueParameter.get());
    }

    getJson() {
        let json = {
            ... super.getJson(),
            value: this.value
        };

        json = saveParameterToJson(json, 'valueParameter', this.valueParameter);
        return json;
    }

    fromJson(json) {
        let bulk = super.fromJson(json);
        bulkUpdate(this.valueParameter, json.value, bulk);
        return {
            ... bulk,
            ... loadParameterFromJson(json, 'valueParameter', this.valueParameter)
        };
    }
}

export class Legato extends RuleEffect {
    constructor(track) {
        super("Legato", track, undefined, LEGATO);
    }
}

export class Staccato extends RuleEffect {
    constructor(track) {
        super("Staccato", track, undefined, STACCATO);
    }
}

export class Any extends RuleEffect {
    constructor(track) {
        super("Any", track, ANY);
    }
}

export class Rest extends RuleEffect {
    constructor(track) {
        super("Rest", track, undefined, REST);
    }
}

export class Piano extends RuleEffect {
    constructor(track) {
        super("Piano", track, undefined, PIANO);
    }
}

export class Pianissimo extends RuleEffect {
    constructor(track) {
        super("Pianissimo", track, undefined, PIANISSIMO);
    }
}

export class Dot extends RuleEffect {
    constructor(track) {
        super("Dot", track, dot());
    }
}

export class Dash1 extends RuleEffect {
    constructor(track) {
        super("Dash 1", track, dash(0));
    }
}

export class Dash2 extends RuleEffect {
    constructor(track) {
        super("Dash 2", track, dash(1));
    }
}

export class Otherwise extends RuleEffect {
    constructor(track) {
        super("Otherwise", track, otherwise());
        this.numberOfOutputs = 1;
    }
}

export class Max extends AuxEffect {
    constructor(track) {
        super("Max", track,
              (x, y) => x.max(y));
        this.value = 1;
    }
}

export class Figure extends AuxEffect {
    constructor(track) {
        super("Figure", track,
              (x, y) => x.figure(y));
        this.value = 0;
    }
}

export class Later extends AuxEffect {
    constructor(track) {
        super("Later", track, (x, y) => x.later(y));
        this.value = 2;
    }
}

export class Skip extends AuxEffect {
    constructor(track) {
        super("Skip", track, (x, y) => x.skip(y));
        this.value = 2;
    }
}

export class REvery extends AuxEffect {
    constructor(track) {
        super("R.Every", track, (x, y) => x.every(y));
        this.value = 2;
    }
}

export class First extends AuxEffect {
    constructor(track) {
        super("First", track, (x, y) => x.first(y));
        this.value = 0;
    }
}

export class Last extends AuxEffect {
    constructor(track) {
        super("Last", track, (x, y) => x.last(y));
        this.value = 0;
    }
}



